import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

import {
  TopbarItem,
  TopbarDivider,
  Button,
  ButtonIcon,
  TopbarDropdown,
  TopbarDropdownItem,
  TopbarDropdownDivider,
  IconTypeUser,
  MessageStatus,
  Spinner,
  IconViewClose,
  IconNavigateMenu,
  IconTypeHelp,
} from '@make.tv/lib-web-uicomponents';
import { createGlobalStyle } from 'styled-components';

import { TopNavRoleGuard, RealRoleGuard } from 'Components/auth/role-guard';
import { ImpersonateModal } from 'Components/auth/impersonate-modal';
import { CurrentTime } from 'Components/current-time';
import { ROLE_NOC } from 'Constants';
import { useRouter, useStorage } from 'Context/hooks';
import { useUser } from 'Context/user';
import { useAuth } from 'Context/auth';
import { useNavContext } from 'Context/nav-context';
import { Users } from 'Services/resources';
import { useOverflowMenu } from './use-overflow-menu';

import {
  AccountId,
  ImpersonationExit,
  MessageStatusContainer,
  Nav,
  PrimaryNavbar,
  LogoArea,
  PortalLogo,
  MessageInfoContainer
} from './styles';
import { SecondaryNavbar } from './secondary-navbar';
import StatusLtnNetwork from './status-ltn-network';

/**
 * Component for rendering the whole header area
 * Including logo, navigation, clock, user dropdown
 */

const GlobalStyle = createGlobalStyle`
  .ltn .dropdown-menu {
    z-index: 9999;
  }
  .primary-navbar {
    width: 100%;
  }
`;

export const TopBar = ({ message }) => {
  const [bannerClosed, setBannerClosed] = useStorage('cookieStorage', 'bannerClosed');

  return (
    <>
      {!bannerClosed && (
        <MessageInfoContainer>
          <MessageStatus level="info">{message}</MessageStatus>
          <ButtonIcon
            color="tertiary"
            icon={<IconViewClose />}
            onClick={() => setBannerClosed(true)}
          />
        </MessageInfoContainer>
      )}
    </>
  );
};

export const PageHeader = () => {
  const {
    user, fetchUser, isImpersonating, impersonatedAccount
  } = useUser();
  const { routes, pathname } = useRouter();
  const { logout } = useAuth();
  const { titleOverwrite, versionOutdated } = useNavContext();
  const [impersonationLoading, setImpersonationLoading] = useState(false);

  const activeRoute = routes.find((d) => pathname.startsWith(`/${d.path}`));

  const {
    visibleItems,
    overflowItems,
    leftRef,
    selectOverflowItem,
    selectVisibleItem,
    rightRef,
    logoRef,
  } = useOverflowMenu(
    routes.filter((route) => route.ui === 'menuLeft'),
    activeRoute
  );

  const exitImpersonation = useCallback((e) => {
    e.stopPropagation();

    if (impersonationLoading) {
      return;
    }

    setImpersonationLoading(true);
    // if passed account id is the same as the user's own account id, remove impersonation
    Users.impersonate(user.user_id, user.account_id)
      .catch(() => {})
      .then(fetchUser)
      .then(() => {
        setImpersonationLoading(false);
        window.location.reload();
      });
  // eslint-disable-next-line
  }, [impersonationLoading, user?.user_id, user?.account_id, fetchUser]);
  return (
    <>
      <GlobalStyle />
      <Nav>
        <PrimaryNavbar
          className="primary-navbar"
          logo={(
            <LogoArea ref={logoRef}>
              <PortalLogo />
            </LogoArea>
          )}
          menuLeft={(
            <div className="ltn top-menu__left" ref={leftRef}>
              {visibleItems.map((route) => (
                <TopNavRoleGuard
                  {...route.auth}
                  key={route.path}
                  title={
                    titleOverwrite?.[route.path]
                      ? `${route.title} (${titleOverwrite[route.path]})`
                      : route.title
                  }
                >
                  <TopbarItem data-path={route.path}>
                    <Link to={`/${route.path}`} onClick={() => selectVisibleItem(route)}>
                      <Button
                        color="tertiary"
                        className={activeRoute?.path === route.path ? '--active' : ''}
                      >
                        {titleOverwrite?.[route.path]
                          ? `${route.title} (${titleOverwrite[route.path]})`
                          : route.title}
                      </Button>
                    </Link>
                  </TopbarItem>
                </TopNavRoleGuard>
              ))}
              <TopbarItem>
                {overflowItems.length > 0 && (
                  <TopbarDropdown
                    position="right"
                    trigger={(
                      <ButtonIcon
                        color="tertiary"
                        icon={<IconNavigateMenu />}
                      />
                    )}
                  >
                    {overflowItems.map((route) => (
                      <Link
                        to={`/${route.path}`}
                        key={route.path}
                        onClick={() => selectOverflowItem(route)}
                      >
                        <TopbarDropdownItem style={{ flexFlow: 'column', height: 'auto' }}>
                          {titleOverwrite?.[route.path]
                            ? `${route.title} (${titleOverwrite[route.path]})`
                            : route.title}
                        </TopbarDropdownItem>
                      </Link>
                    ))}
                  </TopbarDropdown>
                )}
              </TopbarItem>
            </div>
          )}
          menuRight={(
            <div className="d-flex items-center" ref={rightRef}>
              {routes
                .filter((route) => route.ui === 'menuRight')
                .map((route) => (
                  <TopNavRoleGuard {...route.auth} key={route.path}>
                    <TopbarItem>
                      <Link to={`/${route.path}`}>
                        <Button
                          color="tertiary"
                          className={activeRoute?.path === route.path ? '--active' : ''}
                        >
                          {titleOverwrite?.[route.path]
                            ? `${route.title} (${titleOverwrite[route.path]})`
                            : route.title}
                        </Button>
                      </Link>
                    </TopbarItem>
                  </TopNavRoleGuard>
                ))}
              {user && <StatusLtnNetwork />}
              <CurrentTime className="mx-2" />
              <TopbarItem>
                <TopbarDropdown position="right" trigger={<ButtonIcon color="tertiary" icon={<IconTypeHelp />} />}>
                  {routes.filter((route) => route.ui === 'menuHelp').map((route) => (
                    <Link to={`/${route.path}`} key={route.path}>
                      <TopbarDropdownItem>{route.title}</TopbarDropdownItem>
                    </Link>
                  ))}
                </TopbarDropdown>
              </TopbarItem>
              <TopbarDivider />
              {user && (
                <TopbarItem>
                  <TopbarDropdown position="right" trigger={<ButtonIcon color="tertiary" icon={<IconTypeUser />} />}>
                    <TopbarDropdownItem style={{ flexFlow: 'column', height: 'auto' }}>
                      {user.user_id}
                      <div className="d-flex items-center">
                        <AccountId warn={isImpersonating}>
                          {isImpersonating ? impersonatedAccount : user.account_id}
                        </AccountId>
                        {isImpersonating && !impersonationLoading && (
                        <ImpersonationExit onClick={exitImpersonation} />
                        )}
                        {impersonationLoading && (
                        <span className="d-flex items-center" style={{ marginLeft: '5px' }}><Spinner /></span>
                        )}
                      </div>
                    </TopbarDropdownItem>
                    <TopbarDropdownDivider />
                    {routes.filter((route) => route.ui === 'menuUser').map((route) => (
                      <Link to={`/${route.path}`} key={route.path}>
                        <TopbarDropdownItem>
                          {titleOverwrite && titleOverwrite[route.path] ? `${route.title} (${titleOverwrite[route.path]})` : route.title}
                        </TopbarDropdownItem>
                      </Link>
                    ))}
                    <RealRoleGuard role={ROLE_NOC}>
                      <ImpersonateModal>
                        {({ open }) => (
                          <TopbarDropdownItem onClick={open}>
                            Impersonate
                          </TopbarDropdownItem>
                        )}
                      </ImpersonateModal>
                    </RealRoleGuard>
                    <TopbarDropdownItem onClick={logout}>Logout</TopbarDropdownItem>
                  </TopbarDropdown>
                </TopbarItem>
              )}
            </div>
          )}
        />

        <SecondaryNavbar {...{ activeRoute, pathname, titleOverwrite }} />
      </Nav>

      {versionOutdated && (
        <MessageStatusContainer>
          <MessageStatus level="warning" style={{ backgroundColor: 'rgba(0, 0, 0, 0)' }}>
            Clear your browser cache or hard refresh your browser to get the latest version
          </MessageStatus>
        </MessageStatusContainer>
      )}
    </>
  );
};
